import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useApiError } from './useApiError';
import { inviteService, InviteType } from '../services/inviteService';
import { teamService } from '../services/teamService';
import { toast } from 'sonner';
import type { Team, TeamMember, GameDetails } from '../types/team';
import type { TeamInviteFormData } from '../components/team/TeamInviteAcceptModal';
import { useTranslation } from 'react-i18next';
import { ApiResponse } from '@/types/apiResponse';
import { TeamMemberRole } from '@/types/team';
import { RegistrationContext } from '@/types/tournament/game';
import {
  buildRegistrationPayload,
  buildFormDataFromUser,
} from '@/utils/teamRegistration/fieldMapping';

interface InviteParams {
  type: InviteType;
  entityId: string;
  inviteId: string;
  role: string;
}

interface TeamInviteData {
  gameDetails: GameDetails | null;
  name?: string;
  inviteParams: InviteParams;
}

export const useInviteHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { handleApiError, handleApiResponse } = useApiError();
  const { t: tInvite } = useTranslation('invite');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showTeamInviteModal, setShowTeamInviteModal] = useState(false);
  const [teamInviteData, setTeamInviteData] = useState<TeamInviteData | null>(
    null,
  );
  const [pendingInvite, setPendingInvite] = useState<InviteParams | null>(null);
  const [isLoadingTeamData, setIsLoadingTeamData] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [membershipResult, setMembershipResult] = useState<{
    role: string;
    found: boolean;
  } | null>(null);

  const handleInviteRef = useRef<(params: InviteParams) => Promise<void>>();
  const parseInviteParamsRef = useRef<() => InviteParams | null>();

  const parseInviteParams = (): InviteParams | null => {
    const joinOrgId = searchParams.get('joinOrgId');
    const joinClanId = searchParams.get('joinClanId');
    const joinTeamId = searchParams.get('joinTeamId');
    const inviteId = searchParams.get('inviteId');
    const role = searchParams.get('role');

    if (!inviteId || !role) return null;

    if (joinOrgId) {
      return { type: 'organization', entityId: joinOrgId, inviteId, role };
    }
    if (joinClanId) {
      return { type: 'clan', entityId: joinClanId, inviteId, role };
    }
    if (joinTeamId) {
      return { type: 'team', entityId: joinTeamId, inviteId, role };
    }

    return null;
  };

  const checkIfUserAlreadyInTeam = (teamData: Team, userId: string) => {
    if (!teamData?.members) return null;
    const { members } = teamData;

    // Check all member types using TeamMembers interface structure
    const memberTypes = [
      { key: 'players', array: members.players || [] },
      { key: 'substitutes', array: members.substitutes || [] },
      { key: 'coach', array: members.coach || [] },
      { key: 'manager', array: members.manager || [] },
    ] as const;

    for (const { key, array } of memberTypes) {
      const found = array.find((member: TeamMember) => member.id === userId);
      if (found) return { role: key, found: true };
    }

    if (members.captain?.id === userId) {
      return { role: 'captain', found: true };
    }

    return null;
  };

  const handleInvite = async (params: InviteParams) => {
    if (params.type === 'team') {
      if (!isAuthenticated) {
        setPendingInvite(params);
        setShowAuthModal(true);
        toast.error(tInvite('toast.error.loginRequiredTeam'));
        return;
      }
      if (!user?.id) {
        setPendingInvite(params);
        return;
      }
      // For coach/manager roles, check membership first
      const normalizedRole = params.role.toLowerCase() as TeamMemberRole;
      if (
        normalizedRole === TeamMemberRole.COACH ||
        normalizedRole === TeamMemberRole.MANAGER
      ) {
        // First check if user is already in the team
        try {
          const teamDetails = await teamService.getTeamById(params.entityId, {
            inviteId: params.inviteId,
          });
          const team = teamDetails.result?.[0];

          if (!team) {
            toast.error(tInvite('toast.error.loadTeamFailed'));
            clearInviteParams();
            return;
          }

          // Check if user is already in the team
          const membershipCheck = checkIfUserAlreadyInTeam(team, user.id);

          if (membershipCheck?.found) {
            // Show modal with message
            setInviteError(null);
            setMembershipResult(membershipCheck);
            setIsLoadingTeamData(false);
            setTeamInviteData({
              gameDetails: team.gameDetails,
              name: team.name,
              inviteParams: params,
            });
            setShowTeamInviteModal(true);
            return;
          }

          // User is not already a member, proceed with direct acceptance
          if (!team?.gameDetails?.teamRegistrationFields) {
            toast.error(tInvite('toast.error.loadTeamFailed'));
            clearInviteParams();
            return;
          }

          // Filter fields based on roleValidationRules[role][TEAM_INVITE].active
          const activeFields = (
            team.gameDetails.teamRegistrationFields || []
          ).filter(field => {
            const roleRules = field.roleValidationRules?.[normalizedRole];
            if (!roleRules) return false;

            const contextRule = roleRules[RegistrationContext.TEAM_INVITE];
            if (!contextRule) return false;

            return contextRule.active === true;
          });

          // Build form data from user data for active fields only
          const formData = buildFormDataFromUser(user, activeFields);

          // Build dynamic payload using only active fields
          const dynamicPayload = buildRegistrationPayload(
            formData,
            activeFields,
          );

          const payload = {
            inviteId: params.inviteId,
            teamMembers: {
              ...dynamicPayload,
              role: params.role,
            },
          };

          const response = await inviteService.acceptTeamInvite(
            params.entityId,
            payload,
          );

          handleApiResponse(response as ApiResponse<unknown>, {
            onSuccess: data => {
              if (data.result) {
                toast.success(
                  tInvite('toast.success.joinedTeamAs', {
                    role: params.role,
                  }),
                );
                clearInviteParams();
                navigate(`/team/${params.entityId}`);
              } else {
                toast.error(tInvite('toast.error.joinTeamFailed'));
                clearInviteParams();
              }
            },
          });
        } catch (error) {
          handleApiError(error, {
            defaultMessage: tInvite('toast.error.joinTeamFailedRetry'),
          });
          clearInviteParams();
        }
        return;
      }

      // For players/substitutes, show modal with form
      // Show modal immediately with loading state
      setInviteError(null);
      setMembershipResult(null);
      setIsLoadingTeamData(true);
      setTeamInviteData({
        gameDetails: null,
        inviteParams: params,
      });
      setShowTeamInviteModal(true);

      try {
        const teamDetails = await teamService.getTeamById(params.entityId, {
          inviteId: params.inviteId,
        });
        if (teamDetails.result?.[0]) {
          // Check if user is already in the team
          const membershipCheck = checkIfUserAlreadyInTeam(
            teamDetails.result[0],
            user.id,
          );

          if (membershipCheck?.found) {
            setMembershipResult(membershipCheck);
          } else {
            setTeamInviteData({
              gameDetails: teamDetails.result[0].gameDetails,
              name: teamDetails.result[0].name,
              inviteParams: params,
            });
          }
        } else {
          setInviteError('Team not found or invite link is invalid');
        }
      } catch (error) {
        console.error('Error getting team details:', error);
        setInviteError(tInvite('toast.error.loadTeamFailed'));
      } finally {
        setIsLoadingTeamData(false);
      }
      return;
    }

    // Handle clan and organization invites (existing logic)
    if (!isAuthenticated) {
      setPendingInvite(params);
      setShowAuthModal(true);
      toast.error(tInvite('toast.error.loginRequiredGeneric'));
      return;
    }
    if (!user?.id) {
      setPendingInvite(params);
      return;
    }

    try {
      const requestData = {
        inviteId: params.inviteId,
        userId: user.id,
      };

      let response;
      switch (params.type) {
        case 'clan':
          response = await inviteService.checkClanInvite(
            params.inviteId,
            requestData,
          );
          break;
        case 'organization':
          response = await inviteService.checkOrganizationInvite(
            params.inviteId,
            requestData,
          );
          break;
        default:
          throw new Error('Invalid invite type');
      }

      // Use handleApiResponse for consistent error handling
      handleApiResponse(response, {
        onSuccess: data => {
          if (data.result) {
            toast.success(
              tInvite('toast.success.joinedEntity', {
                context: params.type,
                role: params.role,
              }),
            );
            clearInviteParams();

            // Redirect to appropriate page
            switch (params.type) {
              case 'clan':
                navigate(`/clan/${data.result._id}`);
                break;
              case 'organization':
                navigate(`/organization/${data.result._id}`);
                break;
            }
          } else {
            toast.error(tInvite('toast.error.invalidInvite'));
            clearInviteParams();
          }
        },
        onError: () => {
          clearInviteParams();
        },
      });
    } catch (error) {
      console.error('Error processing invite:', error);
      handleApiError(error, {
        defaultMessage: tInvite('toast.error.processFailed'),
      });
    }
  };

  const clearInviteParams = () => {
    searchParams.delete('joinOrgId');
    searchParams.delete('joinClanId');
    searchParams.delete('joinTeamId');
    searchParams.delete('inviteId');
    searchParams.delete('role');
    setSearchParams(searchParams);
    setIsLoadingTeamData(false);
    setInviteError(null);
    setTeamInviteData(null);
    setMembershipResult(null);
  };

  const handleTeamInviteAccept = async (formData: TeamInviteFormData) => {
    if (!teamInviteData?.inviteParams) return;

    try {
      const payload = {
        inviteId: teamInviteData.inviteParams.inviteId,
        teamMembers: {
          ...formData,
          role: teamInviteData.inviteParams.role,
        },
      };

      const response = await inviteService.acceptTeamInvite(
        teamInviteData.inviteParams.entityId,
        payload,
      );

      // Use handleApiResponse to handle both success and error cases
      handleApiResponse(response as ApiResponse<unknown>, {
        onSuccess: data => {
          if (data.result) {
            toast.success(
              tInvite('toast.success.joinedTeamAs', {
                role: teamInviteData.inviteParams.role,
              }),
            );
            setShowTeamInviteModal(false);
            setTeamInviteData(null);
            clearInviteParams();
            navigate(`/team/${teamInviteData.inviteParams.entityId}`);
          } else {
            toast.error(tInvite('toast.error.joinTeamFailed'));
          }
        },
      });
    } catch (error) {
      handleApiError(error, {
        defaultMessage: tInvite('toast.error.joinTeamFailedRetry'),
        onError: () => {
          setShowTeamInviteModal(false);
          setTeamInviteData(null);
          clearInviteParams();
        },
      });
    }
  };

  useEffect(() => {
    handleInviteRef.current = handleInvite;
    parseInviteParamsRef.current = parseInviteParams;
  });

  // Handle authentication success
  useEffect(() => {
    if (isAuthenticated && user && pendingInvite && handleInviteRef.current) {
      setShowAuthModal(false);
      handleInviteRef.current(pendingInvite).finally(() => {
        setPendingInvite(null);
      });
    }
  }, [isAuthenticated, user, pendingInvite]);

  useEffect(() => {
    if (parseInviteParamsRef.current && handleInviteRef.current) {
      const inviteParams = parseInviteParamsRef.current();
      if (inviteParams) {
        handleInviteRef.current(inviteParams);
      }
    }
  }, [searchParams]);

  const handleTeamInviteModalClose = () => {
    setShowTeamInviteModal(false);
    setIsLoadingTeamData(false);
    setInviteError(null);
    setMembershipResult(null);
    setTeamInviteData(null);
    clearInviteParams();
  };

  return {
    parseInviteParams,
    handleInvite,
    showAuthModal,
    setShowAuthModal,
    showTeamInviteModal,
    teamInviteData,
    handleTeamInviteAccept,
    clearInviteParams,
    isLoadingTeamData,
    inviteError,
    membershipResult,
    handleTeamInviteModalClose,
  };
};
